/**
 * Enhanced Error Logging Service
 * Provides comprehensive error logging and reporting for admin monitoring
 * Integrates with LogManagement API for centralized error tracking
 * Supports offline storage and batch reporting
 */

import { apiService } from './api';
import { ErrorLogDto } from '../types/logManagement';
import { loggingHealthService } from './loggingHealthService';

export interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  type: 'api' | 'network' | 'validation' | 'auth' | 'component' | 'unknown';
  message: string;
  persianMessage?: string;
  details?: string;
  stack?: string;
  context?: string;
  userId?: string;
  sessionId?: string;
  userAgent: string;
  url: string;
  statusCode?: number;
  component?: string;
  retryCount?: number;
  resolved?: boolean;
  reportedToServer?: boolean;
}

export interface ErrorContext {
  source?: string;
  category?: string;
  component?: string;
  endpoint?: string;
  additionalData?: Record<string, any>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  tags?: string[];
}

export interface ErrorStatistics {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsByLevel: Record<string, number>;
  errorsByHour: Record<string, number>;
  topErrors: Array<{
    message: string;
    count: number;
    lastOccurrence: string;
  }>;
  recentErrors: ErrorLog[];
  unresolvedErrors: ErrorLog[];
}

export interface ErrorReportPayload {
  errors: ErrorLog[];
  statistics: ErrorStatistics;
  environment: string;
  version: string;
  timestamp: string;
}

export class ErrorLoggingService {
  private readonly maxLocalErrors = 1000;
  private readonly batchSize = 50;
  private readonly reportingInterval = 60000; // 1 minute
  private reportingTimer?: NodeJS.Timeout;
  private isReporting = false;

  constructor() {
    this.startPeriodicReporting();
    this.setupUnloadHandler();
  }

  /**
   * Log an error with full context and enhanced LogManagement integration
   */
  logError(
    error: Error | string,
    type: ErrorLog['type'] = 'unknown',
    context?: string | ErrorContext,
    additionalData?: Partial<ErrorLog>
  ): string {
    const errorId = this.generateErrorId();
    
    // Handle both old string context and new ErrorContext
    const errorContext = typeof context === 'string' ? { source: 'Unknown', context } : context || {};
    
    // Create base error log
    const errorLog: ErrorLog = {
      id: errorId,
      timestamp: new Date().toISOString(),
      level: 'error',
      type,
      message: typeof error === 'string' ? error : error.message,
      userAgent: navigator.userAgent,
      url: window.location.href,
      reportedToServer: false,
      resolved: false
    };

    // Apply additional data, but ensure resolved remains boolean
    if (additionalData) {
      const { resolved, reportedToServer, ...otherData } = additionalData;
      
      // Apply other data first
      Object.keys(otherData).forEach(key => {
        if (key !== 'resolved' && key !== 'reportedToServer') {
          (errorLog as any)[key] = (otherData as any)[key];
        }
      });
      
      // Force resolved to be boolean - default to false if undefined
      if (typeof resolved === 'boolean') {
        errorLog.resolved = resolved;
      }
      
      // Force reportedToServer to be boolean - default to false if undefined
      if (typeof reportedToServer === 'boolean') {
        errorLog.reportedToServer = reportedToServer;
      }
    }

    // Only add optional fields if they have values
    if (typeof error === 'object' && error.toString) {
      errorLog.details = error.toString();
    } else if (typeof context === 'string') {
      errorLog.details = context;
    } else if (errorContext.source) {
      errorLog.details = errorContext.source;
    }

    if (typeof error === 'object' && error.stack) {
      errorLog.stack = error.stack;
    }

    if (typeof context === 'string') {
      errorLog.context = context;
    } else if (errorContext.source) {
      errorLog.context = errorContext.source;
    }

    const userId = this.getCurrentUserId();
    if (userId) errorLog.userId = userId;

    const sessionId = this.getSessionId();
    if (sessionId) errorLog.sessionId = sessionId;

    if (typeof context !== 'string' && context?.component) {
      errorLog.component = context.component;
    }

    this.storeErrorLocally(errorLog);
    this.logToConsole(errorLog);

    // Enhanced immediate reporting for critical errors
    if (this.isCriticalError(type, typeof context !== 'string' ? context?.severity : undefined)) {
      this.reportErrorImmediatelyToLogManagement(errorLog, typeof context === 'string' ? undefined : context);
    } else {
      // Add to batch queue for non-critical errors
      this.addToBatchQueue(errorLog, typeof context === 'string' ? undefined : context);
    }

    return errorId;
  }

  /**
   * Log a warning with enhanced context support
   */
  logWarning(
    message: string,
    type: ErrorLog['type'] = 'unknown',
    context?: string | ErrorContext,
    additionalData?: Partial<ErrorLog>
  ): string {
    return this.logError(message, type, context, {
      level: 'warning',
      ...additionalData
    });
  }

  /**
   * Log an info message with enhanced context support
   */
  logInfo(
    message: string,
    type: ErrorLog['type'] = 'unknown',
    context?: string | ErrorContext,
    additionalData?: Partial<ErrorLog>
  ): string {
    return this.logError(message, type, context, {
      level: 'info',
      ...additionalData
    });
  }

  /**
   * Log an error with categorization for better organization
   */
  logCategorizedError(
    error: Error | string,
    category: string,
    source: string,
    context?: ErrorContext
  ): string {
    const enhancedContext: ErrorContext = {
      ...context,
      category,
      source
    };

    return this.logError(error, 'unknown', enhancedContext);
  }

  /**
   * Log API-specific errors with enhanced context
   */
  logApiError(
    error: Error | string,
    endpoint: string,
    statusCode?: number,
    context?: ErrorContext
  ): string {
    const enhancedContext: ErrorContext = {
      ...context,
      endpoint,
      category: 'api_error',
      source: 'ApiService',
      additionalData: {
        ...context?.additionalData,
        statusCode,
        endpoint
      }
    };

    return this.logError(error, 'api', enhancedContext, statusCode ? { statusCode } : {});
  }

  /**
   * Log component-specific errors
   */
  logComponentError(
    error: Error | string,
    component: string,
    context?: ErrorContext
  ): string {
    const enhancedContext: ErrorContext = {
      ...context,
      component,
      category: 'component_error',
      source: 'ComponentError'
    };

    return this.logError(error, 'component', enhancedContext);
  }

  /**
   * Mark an error as resolved
   */
  resolveError(errorId: string): void {
    try {
      const errors = this.getStoredErrors();
      const errorIndex = errors.findIndex(e => e.id === errorId);
      
      if (errorIndex !== -1 && errors[errorIndex]) {
        const error = errors[errorIndex]!;
        error.resolved = true;
        localStorage.setItem('errorLogs', JSON.stringify(errors));
      }
    } catch (error) {
      console.warn('Failed to resolve error:', error);
    }
  }

  /**
   * Get error statistics for admin dashboard
   */
  getErrorStatistics(): ErrorStatistics {
    try {
      const errors = this.getStoredErrors();
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      // Filter recent errors
      const recentErrors = errors.filter(e => new Date(e.timestamp) > last24Hours);

      // Count by type
      const errorsByType: Record<string, number> = {};
      const errorsByLevel: Record<string, number> = {};
      const errorsByHour: Record<string, number> = {};
      const errorCounts: Record<string, number> = {};

      recentErrors.forEach(error => {
        // By type
        errorsByType[error.type] = (errorsByType[error.type] || 0) + 1;
        
        // By level
        errorsByLevel[error.level] = (errorsByLevel[error.level] || 0) + 1;
        
        // By hour
        const hour = new Date(error.timestamp).getHours().toString();
        errorsByHour[hour] = (errorsByHour[hour] || 0) + 1;
        
        // Count occurrences
        errorCounts[error.message] = (errorCounts[error.message] || 0) + 1;
      });

      // Top errors
      const topErrors = Object.entries(errorCounts)
        .map(([message, count]) => ({
          message,
          count,
          lastOccurrence: recentErrors
            .filter(e => e.message === message)
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
            ?.timestamp || ''
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return {
        totalErrors: recentErrors.length,
        errorsByType,
        errorsByLevel,
        errorsByHour,
        topErrors,
        recentErrors: recentErrors.slice(-20),
        unresolvedErrors: errors.filter(e => !e.resolved).slice(-50)
      };
    } catch (error) {
      console.warn('Failed to generate error statistics:', error);
      return {
        totalErrors: 0,
        errorsByType: {},
        errorsByLevel: {},
        errorsByHour: {},
        topErrors: [],
        recentErrors: [],
        unresolvedErrors: []
      };
    }
  }

  /**
   * Get all stored errors
   */
  getAllErrors(): ErrorLog[] {
    return this.getStoredErrors();
  }

  /**
   * Clear all stored errors
   */
  clearErrors(): void {
    localStorage.removeItem('errorLogs');
  }

  /**
   * Export errors for admin download
   */
  exportErrors(): string {
    const errors = this.getStoredErrors();
    const statistics = this.getErrorStatistics();
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      environment: import.meta.env.MODE,
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      statistics,
      errors
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import errors (for testing or data recovery)
   */
  importErrors(jsonData: string): void {
    try {
      const data = JSON.parse(jsonData);
      if (data.errors && Array.isArray(data.errors)) {
        localStorage.setItem('errorLogs', JSON.stringify(data.errors));
      }
    } catch (error) {
      throw new Error('Invalid error data format');
    }
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID
   */
  private getCurrentUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Get session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Store error in local storage
   */
  private storeErrorLocally(errorLog: ErrorLog): void {
    try {
      const errors = this.getStoredErrors();
      errors.push(errorLog);

      // Keep only the most recent errors
      if (errors.length > this.maxLocalErrors) {
        errors.splice(0, errors.length - this.maxLocalErrors);
      }

      localStorage.setItem('errorLogs', JSON.stringify(errors));
    } catch (error) {
      console.warn('Failed to store error locally:', error);
    }
  }

  /**
   * Get stored errors from local storage
   */
  private getStoredErrors(): ErrorLog[] {
    try {
      return JSON.parse(localStorage.getItem('errorLogs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Log error to console with formatting
   */
  private logToConsole(errorLog: ErrorLog): void {
    const style = this.getConsoleStyle(errorLog.level);
    console.group(`%c${errorLog.level.toUpperCase()}: ${errorLog.message}`, style);
    console.log('Type:', errorLog.type);
    console.log('Context:', errorLog.context);
    console.log('Timestamp:', errorLog.timestamp);
    if (errorLog.details) console.log('Details:', errorLog.details);
    if (errorLog.stack) console.log('Stack:', errorLog.stack);
    console.groupEnd();
  }

  /**
   * Get console styling for different error levels
   */
  private getConsoleStyle(level: ErrorLog['level']): string {
    switch (level) {
      case 'error':
        return 'color: #dc2626; font-weight: bold;';
      case 'warning':
        return 'color: #d97706; font-weight: bold;';
      case 'info':
        return 'color: #2563eb; font-weight: bold;';
      default:
        return 'color: #374151; font-weight: bold;';
    }
  }



  /**
   * Start periodic error reporting
   */
  private startPeriodicReporting(): void {
    this.reportingTimer = setInterval(() => {
      this.reportPendingErrors();
    }, this.reportingInterval);
  }

  /**
   * Report pending errors to LogManagement API
   */
  private async reportPendingErrors(): Promise<void> {
    if (this.isReporting || !navigator.onLine) return;

    try {
      this.isReporting = true;
      
      // Process both old unreported errors and new batch queue
      await this.processBatchQueueForLogManagement();
      
      const errors = this.getStoredErrors();
      const unreportedErrors = errors.filter(e => !e.reportedToServer);

      if (unreportedErrors.length === 0) return;

      // Send unreported errors to LogManagement
      for (let i = 0; i < unreportedErrors.length; i += this.batchSize) {
        const batch = unreportedErrors.slice(i, i + this.batchSize);
        try {
          for (const error of batch) {
            await this.sendSingleErrorToLogManagement(error);
          }
          this.markAsReported(batch.map(e => e.id));
        } catch (error) {
          console.warn('ErrorLoggingService: Failed to report error batch to LogManagement:', error);
          break; // Stop trying if one batch fails
        }
      }
    } catch (error) {
      console.warn('ErrorLoggingService: Error during periodic reporting to LogManagement:', error);
    } finally {
      this.isReporting = false;
    }
  }



  /**
   * Send single error to LogManagement API with comprehensive error handling
   */
  private async sendSingleErrorToLogManagement(
    error: ErrorLog,
    context?: ErrorContext
  ): Promise<void> {
    try {
      // Check circuit breaker before attempting to send
      if (this.isErrorLoggingCircuitBreakerOpen()) {
        console.warn('ErrorLoggingService: Circuit breaker open, skipping error log to prevent further failures');
        return;
      }

      const errorLogDto: ErrorLogDto = {
        id: error.id,
        timestamp: error.timestamp,
        level: error.level as 'error' | 'warning' | 'info',
        message: error.message,
        source: context?.source || 'ErrorLoggingService',
        category: context?.category || 'application_error',
        resolved: error.resolved || false,
        tags: this.generateErrorTags(error, context),
        metadata: {
          type: error.type,
          details: error.details,
          context: error.context,
          userAgent: error.userAgent,
          url: error.url,
          statusCode: error.statusCode,
          component: error.component,
          retryCount: error.retryCount,
          severity: context?.severity || 'medium',
          ...context?.additionalData
        }
      };

      // Only add optional fields if they have values
      if (error.userId) errorLogDto.userId = error.userId;
      if (error.stack) errorLogDto.stackTrace = error.stack;
      if (context?.endpoint) errorLogDto.endpoint = context.endpoint;
      
      const clientIP = await this.getClientIP();
      if (clientIP) errorLogDto.ipAddress = clientIP;

      await apiService.post('/api/LogManagement/error-logs', errorLogDto);
      console.log('ErrorLoggingService: Successfully sent error to LogManagement:', error.id);
      
      // Record successful logging to reset circuit breaker
      this.recordSuccessfulErrorLogging();
    } catch (apiError) {
      // Comprehensive error handling - don't throw to prevent breaking main flow
      this.handleErrorLoggingError(apiError, error, context);
      throw apiError; // Re-throw for batch processing logic
    }
  }

  /**
   * Report error immediately to LogManagement API with endpoint availability checks
   */
  private async reportErrorImmediatelyToLogManagement(
    errorLog: ErrorLog,
    context?: ErrorContext
  ): Promise<void> {
    try {
      // Check endpoint availability before attempting to send
      const serviceHealth = loggingHealthService.getServiceHealth('errorLoggingService');
      if (!serviceHealth.endpointAvailable && !serviceHealth.isHealthy) {
        console.warn('ErrorLoggingService: Endpoint unavailable, storing for batch processing');
        this.storeForBatchProcessing(errorLog, context);
        return;
      }

      await this.sendSingleErrorToLogManagement(errorLog, context);
      this.markAsReported([errorLog.id]);
    } catch (error) {
      console.warn('ErrorLoggingService: Failed to report error immediately to LogManagement:', error);
      // Store for batch processing
      this.storeForBatchProcessing(errorLog, context);
    }
  }

  /**
   * Add error to batch queue for LogManagement processing
   */
  private addToBatchQueue(errorLog: ErrorLog, context?: ErrorContext): void {
    this.storeForBatchProcessing(errorLog, context);
  }

  /**
   * Store error for batch processing
   */
  private storeForBatchProcessing(errorLog: ErrorLog, context?: ErrorContext): void {
    try {
      const batchQueue = this.getBatchQueue();
      const queueItem: { error: ErrorLog; context?: ErrorContext } = { error: errorLog };
      if (context) queueItem.context = context;
      
      batchQueue.push(queueItem);
      
      // Keep only the most recent 50 items in batch queue
      if (batchQueue.length > 50) {
        batchQueue.splice(0, batchQueue.length - 50);
      }
      
      localStorage.setItem('errorBatchQueue', JSON.stringify(batchQueue));
    } catch (error) {
      console.error('ErrorLoggingService: Failed to store error for batch processing:', error);
    }
  }

  /**
   * Get batch queue from storage
   */
  private getBatchQueue(): Array<{ error: ErrorLog; context?: ErrorContext }> {
    try {
      return JSON.parse(localStorage.getItem('errorBatchQueue') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Process batch queue for LogManagement
   */
  private async processBatchQueueForLogManagement(): Promise<void> {
    const batchQueue = this.getBatchQueue();
    if (batchQueue.length === 0) return;

    console.log(`ErrorLoggingService: Processing batch queue of ${batchQueue.length} errors`);

    const processedIds: string[] = [];
    
    try {
      for (const { error, context } of batchQueue) {
        try {
          await this.sendSingleErrorToLogManagement(error, context);
          processedIds.push(error.id);
        } catch (itemError) {
          console.error(`ErrorLoggingService: Failed to process error ${error.id}:`, itemError);
          // Continue processing other items
        }
      }

      // Remove processed items from queue
      if (processedIds.length > 0) {
        const remainingQueue = batchQueue.filter(
          item => !processedIds.includes(item.error.id)
        );
        localStorage.setItem('errorBatchQueue', JSON.stringify(remainingQueue));
        this.markAsReported(processedIds);
        
        console.log(`ErrorLoggingService: Successfully processed ${processedIds.length} errors from batch queue`);
      }
    } catch (error) {
      console.error('ErrorLoggingService: Batch processing failed:', error);
    }
  }

  /**
   * Mark errors as reported
   */
  private markAsReported(errorIds: string[]): void {
    try {
      const errors = this.getStoredErrors();
      let updated = false;

      errors.forEach(error => {
        if (errorIds.includes(error.id) && !error.reportedToServer) {
          error.reportedToServer = true;
          updated = true;
        }
      });

      if (updated) {
        localStorage.setItem('errorLogs', JSON.stringify(errors));
      }
    } catch (error) {
      console.warn('Failed to mark errors as reported:', error);
    }
  }

  /**
   * Setup handler for page unload to send remaining errors
   */
  private setupUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      // Try to send any remaining errors using sendBeacon
      const unreportedErrors = this.getStoredErrors().filter(e => !e.reportedToServer);
      const batchQueue = this.getBatchQueue();
      
      if (unreportedErrors.length > 0 || batchQueue.length > 0) {
        // Send the most critical errors first
        const errorsToSend = unreportedErrors.slice(0, 5); // Limit to 5 for beacon size
        
        for (const error of errorsToSend) {
          const errorLogDto: ErrorLogDto = {
            id: error.id,
            timestamp: error.timestamp,
            level: error.level as 'error' | 'warning' | 'info',
            message: error.message,
            source: 'ErrorLoggingService',
            category: 'beacon_report',
            resolved: false,
            tags: ['beacon-report', 'error-logging-service', 'page-unload'],
            metadata: {
              type: error.type,
              details: error.details,
              context: error.context,
              userAgent: error.userAgent,
              url: error.url,
              statusCode: error.statusCode,
              component: error.component,
              beaconReport: true
            }
          };

          // Only add optional fields if they exist
          if (error.userId) errorLogDto.userId = error.userId;
          if (error.stack) errorLogDto.stackTrace = error.stack;
          
          try {
            navigator.sendBeacon(
              '/api/LogManagement/error-logs',
              JSON.stringify(errorLogDto)
            );
          } catch (beaconError) {
            console.error('ErrorLoggingService: Failed to send beacon report:', beaconError);
          }
        }
      }
    });
  }

  /**
   * Check if error is critical and should be reported immediately
   */
  private isCriticalError(type: ErrorLog['type'], severity?: string): boolean {
    const criticalTypes = ['auth', 'api'];
    const criticalSeverities = ['high', 'critical'];
    
    return criticalTypes.includes(type) || 
           (severity && criticalSeverities.includes(severity));
  }

  /**
   * Generate tags for error categorization
   */
  private generateErrorTags(error: ErrorLog, context?: ErrorContext): string[] {
    const tags = ['error-logging', error.type, error.level];
    
    if (context?.category) tags.push(`category:${context.category}`);
    if (context?.source) tags.push(`source:${context.source}`);
    if (context?.component) tags.push(`component:${context.component}`);
    if (context?.severity) tags.push(`severity:${context.severity}`);
    if (error.userId) tags.push(`user:${error.userId}`);
    if (context?.tags) tags.push(...context.tags);
    
    return tags;
  }

  /**
   * Handle error logging errors with comprehensive graceful degradation
   */
  private handleErrorLoggingError(apiError: any, error: ErrorLog, _context?: ErrorContext): void {
    const errorInfo = {
      errorId: error.id,
      errorMessage: error.message,
      apiError: apiError?.message || 'Unknown error',
      statusCode: apiError?.response?.status,
      responseData: apiError?.response?.data,
      timestamp: new Date().toISOString(),
      url: apiError?.config?.url || '/api/LogManagement/error-logs'
    };

    // Enhanced console logging for debugging without throwing exceptions
    console.error('ErrorLoggingService: Failed to send error to LogManagement:', errorInfo);
    
    // Log detailed error information for debugging
    if (apiError?.response?.data) {
      console.error('ErrorLoggingService: Backend response details:', apiError.response.data);
    }
    
    if (apiError?.config) {
      console.error('ErrorLoggingService: Request configuration:', {
        method: apiError.config.method,
        url: apiError.config.url,
        headers: apiError.config.headers
      });
    }

    // Check error logging service health
    this.checkErrorLoggingHealth(apiError);
    
    // Store failed error log for debugging
    this.storeFailedErrorLog(error, apiError);
    
    // Ensure main application flow continues regardless of logging failures
    // This is critical for non-blocking behavior
  }

  /**
   * Record successful error logging to reset circuit breaker
   */
  private recordSuccessfulErrorLogging(): void {
    const healthKey = 'errorLoggingHealth';
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      // Reset failure count on successful request
      if (healthData.failures > 0) {
        healthData.failures = Math.max(0, healthData.failures - 1);
        healthData.lastSuccess = Date.now();
        
        // Close circuit breaker if it was open and we have a success
        if (healthData.circuitOpen) {
          healthData.circuitOpen = false;
          console.log('ErrorLoggingService: Circuit breaker closed after successful request');
        }
        
        localStorage.setItem(healthKey, JSON.stringify(healthData));
      }
    } catch (storageError) {
      console.error('ErrorLoggingService: Failed to record successful error logging:', storageError);
    }
  }

  /**
   * Check if error logging circuit breaker is open
   */
  private isErrorLoggingCircuitBreakerOpen(): boolean {
    try {
      const healthData = JSON.parse(localStorage.getItem('errorLoggingHealth') || '{}');
      return healthData.circuitOpen || false;
    } catch {
      return false;
    }
  }

  /**
   * Store failed error logs for debugging
   */
  private storeFailedErrorLog(errorLog: ErrorLog, apiError: any): void {
    try {
      const failedLogs = this.getFailedErrorLogs();
      failedLogs.push({
        log: errorLog,
        error: {
          message: apiError?.message,
          statusCode: apiError?.response?.status,
          responseData: apiError?.response?.data
        },
        timestamp: new Date().toISOString()
      });

      // Keep only the most recent 25 failed logs
      if (failedLogs.length > 25) {
        failedLogs.splice(0, failedLogs.length - 25);
      }

      localStorage.setItem('failedErrorLogs', JSON.stringify(failedLogs));
    } catch (storageError) {
      console.error('ErrorLoggingService: Failed to store failed error log:', storageError);
    }
  }

  /**
   * Get failed error logs from storage
   */
  private getFailedErrorLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem('failedErrorLogs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Check error logging service health with enhanced circuit breaker
   */
  private checkErrorLoggingHealth(apiError: any): void {
    const healthKey = 'errorLoggingHealth';
    const now = Date.now();
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      if (!healthData.failures) {
        healthData.failures = 0;
        healthData.lastFailure = now;
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
      }
      
      // Increment failure counts
      healthData.failures++;
      healthData.consecutiveFailures = (healthData.consecutiveFailures || 0) + 1;
      healthData.lastFailure = now;
      
      // Enhanced circuit breaker logic
      const shouldOpenCircuit = 
        healthData.consecutiveFailures >= 5 || // 5 consecutive failures
        (healthData.failures >= 10 && (now - (healthData.lastSuccess || 0)) > 60000); // 10 failures in 1 minute
      
      if (shouldOpenCircuit && !healthData.circuitOpen) {
        healthData.circuitOpen = true;
        healthData.circuitOpenedAt = now;
        console.warn('ErrorLoggingService: Circuit breaker opened due to repeated failures', {
          consecutiveFailures: healthData.consecutiveFailures,
          totalFailures: healthData.failures,
          errorType: apiError?.response?.status || 'network'
        });
      }
      
      // Auto-reset circuit breaker after timeout with exponential backoff
      const backoffTime = Math.min(300000, 60000 * Math.pow(2, Math.floor(healthData.failures / 5))); // Max 5 minutes
      if (healthData.circuitOpen && (now - healthData.circuitOpenedAt) > backoffTime) {
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
        console.log('ErrorLoggingService: Circuit breaker reset after timeout', {
          backoffTime: backoffTime / 1000 + 's'
        });
      }
      
      localStorage.setItem(healthKey, JSON.stringify(healthData));
    } catch (storageError) {
      console.error('ErrorLoggingService: Failed to update health data:', storageError);
      // Ensure main application flow continues even if health tracking fails
    }
  }

  /**
   * Get client IP address (best effort)
   */
  private async getClientIP(): Promise<string> {
    try {
      // In a real application, you might get this from a service or header
      // For now, return a placeholder
      return 'client-ip';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Enhanced error reporting with offline support
   */
  async reportErrorsWhenOnline(): Promise<void> {
    if (!navigator.onLine) return;
    
    try {
      await this.processBatchQueueForLogManagement();
      await this.reportPendingErrors();
    } catch (error) {
      console.error('ErrorLoggingService: Failed to report errors when online:', error);
    }
  }

  /**
   * Get error statistics for LogManagement integration
   */
  getEnhancedErrorStatistics(): ErrorStatistics & {
    batchQueueSize: number;
    unreportedCount: number;
    logManagementIntegration: boolean;
  } {
    const baseStats = this.getErrorStatistics();
    const batchQueue = this.getBatchQueue();
    const unreportedErrors = this.getStoredErrors().filter(e => !e.reportedToServer);
    
    return {
      ...baseStats,
      batchQueueSize: batchQueue.length,
      unreportedCount: unreportedErrors.length,
      logManagementIntegration: true
    };
  }

  /**
   * Force flush all pending errors to LogManagement
   */
  async flushToLogManagement(): Promise<void> {
    console.log('ErrorLoggingService: Force flushing all pending errors to LogManagement');
    
    try {
      await this.processBatchQueueForLogManagement();
      await this.reportPendingErrors();
      console.log('ErrorLoggingService: Successfully flushed all pending errors');
    } catch (error) {
      console.error('ErrorLoggingService: Failed to flush errors to LogManagement:', error);
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.reportingTimer) {
      clearInterval(this.reportingTimer);
    }
    
    // Try to flush any remaining errors
    this.flushToLogManagement().catch(error => {
      console.error('ErrorLoggingService: Failed to flush errors during cleanup:', error);
    });
  }
}

// Export singleton instance
export const errorLoggingService = new ErrorLoggingService();

export default ErrorLoggingService;